import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { PointTransaction } from "../../models/PointTransaction";
import { User } from "../../models/Users";
import { fetchData, postData, putData } from '../../services/ApiService';
import {
  closeCircularProgress,
  openCircularProgress,
} from "../uireducers/progress";
import { ChangePassword } from "../../models/ChangePassword";

// Logic to Get All Users
export const getAllUsers = createAsyncThunk(
  "userEntity/getAllUsers",
  async (offset: number, thunkAPI) => {
    thunkAPI.dispatch(openCircularProgress());

    try {
      const response = await fetchData(`users?offset=${offset}`);
      thunkAPI.dispatch(closeCircularProgress());
      return response;
    } catch (error: any) {
      thunkAPI.dispatch(closeCircularProgress());
      return thunkAPI.rejectWithValue(error?.message || 'Failed to fetch users');
    }
  }
);

export const changePassword = createAsyncThunk(
  "userEntity/changePassword",
  async (request: ChangePassword, thunkAPI) => {
    thunkAPI.dispatch(openCircularProgress());

    try {
      const response = await postData(`users/changePassword`, request);
      thunkAPI.dispatch(closeCircularProgress());
      return response;
    } catch (error: any) {
      thunkAPI.dispatch(closeCircularProgress());
      return thunkAPI.rejectWithValue(error?.message || 'Failed to change password');
    }
  }
);

// Logic to Get All Admins
export const getAllAdmins = createAsyncThunk(
  "userEntity/getAllAdmins",
  async (data, thunkAPI) => {
    thunkAPI.dispatch(openCircularProgress());

    try {
      const response = await fetchData("users/admins");
      thunkAPI.dispatch(closeCircularProgress());
      return response;
    } catch (error: any) {
      thunkAPI.dispatch(closeCircularProgress());
      return thunkAPI.rejectWithValue(error?.message || 'Failed to fetch admins');
    }
  }
);

// Logic to get all transaction points
export const getTransactionPoints = createAsyncThunk(
  "userEntity/getTransactionPoints",
  async (offset: number, thunkAPI) => {
    thunkAPI.dispatch(openCircularProgress());

    try {
      const response = await fetchData(`users/points/transactions?offset=${offset}`);
      thunkAPI.dispatch(closeCircularProgress());
      return response;
    } catch (error: any) {
      thunkAPI.dispatch(closeCircularProgress());
      return thunkAPI.rejectWithValue(error?.message || 'Failed to fetch transactions');
    }
  }
);

// Logic to save user
export const saveUser = createAsyncThunk(
  "userEntity/saveUser",
  async (data: User, thunkAPI) => {
    thunkAPI.dispatch(openCircularProgress());

    try {
      const response = await postData(`users`, data);
      thunkAPI.dispatch(closeCircularProgress());
      return response;
    } catch (error: any) {
      thunkAPI.dispatch(closeCircularProgress());
      return thunkAPI.rejectWithValue(error?.message || 'Failed to create user');
    }
  }
);

// Logic to add points
export const addPoints = createAsyncThunk(
  "userEntity/addPoints",
  async ({user, point}: {user: User, point: number}, thunkAPI) => {
    thunkAPI.dispatch(openCircularProgress());

    try {
      const response = await postData(`users/${user.id}/addPoints/${point}`, null);
      thunkAPI.dispatch(closeCircularProgress());
      return response;
    } catch (error: any) {
      thunkAPI.dispatch(closeCircularProgress());
      return thunkAPI.rejectWithValue(error?.message || 'Failed to add points');
    }
  }
);

// Logic to revoke points
export const revokePoints = createAsyncThunk(
  "userEntity/revokePoints",
  async ({user, point}: {user: User, point: number}, thunkAPI) => {
    thunkAPI.dispatch(openCircularProgress());

    try {
      const response = await postData(`users/${user.id}/revokePoints/${point}`, null);
      thunkAPI.dispatch(closeCircularProgress());
      return response;
    } catch (error: any) {
      thunkAPI.dispatch(closeCircularProgress());
      return thunkAPI.rejectWithValue(error?.message || 'Failed to revoke points');
    }
  }
);

// Logic to toggle user status
export const toggleUserStatus = createAsyncThunk(
  "userEntity/toggleUserStatus",
  async (userId: number, thunkAPI) => {
    thunkAPI.dispatch(openCircularProgress());

    try {
      const response = await fetchData(`users/${userId}/toggleStatus`);
      thunkAPI.dispatch(closeCircularProgress());
      return response;
    } catch (error: any) {
      thunkAPI.dispatch(closeCircularProgress());
      return thunkAPI.rejectWithValue(error?.message || 'Failed to update user status');
    }
  }
);

// Logic to update user
export const updateUser = createAsyncThunk(
  "userEntity/updateUser",
  async (user: User, thunkAPI) => {
    thunkAPI.dispatch(openCircularProgress());

    try {
      const response = await putData(`users/${user.id}`, user);
      thunkAPI.dispatch(closeCircularProgress());
      return response;
    } catch (error: any) {
      thunkAPI.dispatch(closeCircularProgress());
      return thunkAPI.rejectWithValue(error?.message || 'Failed to update user');
    }
  }
);

// Logic to get user balance
export const getUserBalance = createAsyncThunk(
  "userEntity/getUserBalance",
  async (user, thunkAPI) => {
    thunkAPI.dispatch(openCircularProgress());

    try {
      const response = await fetchData(`users/points`);
      thunkAPI.dispatch(closeCircularProgress());
      return response;
    } catch (error: any) {
      thunkAPI.dispatch(closeCircularProgress());
      return thunkAPI.rejectWithValue(error?.message || 'Failed to fetch balance');
    }
  }
);

// Logic to get users by role
export const getUsersByRole = createAsyncThunk(
  "userEntity/getUsersByRole",
  async (role: string, thunkAPI) => {
    thunkAPI.dispatch(openCircularProgress());

    try {
      const response = await fetchData(`users/roles/${role}`);
      thunkAPI.dispatch(closeCircularProgress());
      return response;
    } catch (error: any) {
      thunkAPI.dispatch(closeCircularProgress());
      return thunkAPI.rejectWithValue(error?.message || 'Failed to fetch users by role');
    }
  }
);

const slice = createSlice({
  name: "userEntity",
  initialState: {
    user: {} as User,
    users: [] as User[],
    admins: [] as User[],
    selectedUser: null as User | null,
    userPoint: 0 as number,
    pointTransactions: {
      content: [] as PointTransaction[],
      empty: false,
      first: false,
      last: false,
      number: 0,
      numberOfElements: 0,
      pageable: {},
      size: 0,
      totalElements: 0,
      totalPages: 0,
    },
    paginationData: {
      currentPage: 0,
      totalCount: 0,
      totalPages: 0,
      pageSize: 10,
    },
  },
  reducers: {
    setSelectedUser: (state, action) => {
      state.selectedUser = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getAllUsers.fulfilled, (state, action) => {
      const payload = action.payload;
      if (Array.isArray(payload)) {
        state.users = payload;
      } else if (payload && Array.isArray(payload.content)) {
        state.users = payload.content;
      } else if (payload && Array.isArray(payload.data)) {
        state.users = payload.data;
      } else {
        state.users = [];
      }
      state.paginationData = {
        currentPage: payload?.number ?? 0,
        totalCount: payload?.totalElements ?? state.users.length,
        totalPages: payload?.totalPages ?? 1,
        pageSize: payload?.size ?? 10,
      };
    });
    builder.addCase(getTransactionPoints.fulfilled, (state, action) => {
      const content = Array.isArray(action.payload?.content)
        ? [...action.payload.content]
        : Array.isArray(action.payload)
        ? [...action.payload]
        : [];
      state.pointTransactions = {
        ...state.pointTransactions,
        ...(action.payload && typeof action.payload === 'object' ? action.payload : {}),
        content,
      };
      state.paginationData = {
        currentPage: action.payload?.number ?? 0,
        totalCount: action.payload?.totalElements ?? content.length,
        totalPages: action.payload?.totalPages ?? 1,
        pageSize: action.payload?.size ?? 10,
      };
    });
    builder.addCase(saveUser.fulfilled, (state, action) => {
      const currentUsers = Array.isArray(state.users) ? state.users : [];
      state.users = [...currentUsers, action.payload];
    });
    builder.addCase(addPoints.fulfilled, (state, action) => {});
    builder.addCase(revokePoints.fulfilled, (state, action) => {});
    builder.addCase(getAllAdmins.fulfilled, (state, action) => {
      state.admins = Array.isArray(action.payload)
        ? action.payload
        : (action.payload?.content || action.payload?.data || []);
    });
    builder.addCase(getUserBalance.fulfilled, (state, action) => {
      state.userPoint = action.payload;
    });
    builder.addCase(getUsersByRole.fulfilled, (state, action) => {
      const payload = action.payload;
      if (Array.isArray(payload)) {
        state.users = payload;
      } else if (payload && Array.isArray(payload.content)) {
        state.users = payload.content;
      } else if (payload && Array.isArray(payload.data)) {
        state.users = payload.data;
      } else {
        state.users = [];
      }
    });
  },
});

export const { setSelectedUser } = slice.actions;
export default slice.reducer;